import {
   Flex,
   Box,
   Heading,
   Image,
   Text,
   Link,
   Button,
   Select,
   Skeleton,
   Stack,
} from '@chakra-ui/react';
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Pagination from 'Chakra/Pagination';
import { _js } from '@ifixit/localize';
import groupBy from 'lodash/groupBy';

export function Activity({ userid, data }: { userid: number; data: any }) {
   const { filters, pageContext: initialPageContext, activityTimelineURI: timelineImg } = data;

   return (
      <Flex direction="column">
         <ActiveHours image={timelineImg} />
         <ActivityFilterView
            userid={userid}
            filters={filters}
            initialPageContext={initialPageContext}
         />
      </Flex>
   );
}

const ActiveHours = ({ image }: { image: string }) => (
   <Box display={{ base: 'none', md: 'unset' }}>
      <Heading
         as="h2"
         m="0 0 8px"
         fontSize="22px"
         fontWeight="semibold"
         borderBottomWidth="4px"
         borderColor="gray.100"
      >
         {_js('Active Hours')}
      </Heading>
      <Image minH={65} w={590} src={image} />
      <Text>
         {_js(
            'The time of day that you have been active over the last 90 days. Each line on this graph \
            is an action you performed on the site (we only count major actions, like voting and \
            editing posts). The numbers on the bottom of the graph are the hour of the day in '
         )}
         <Link href="https://greenwichmeantime.com/time-zone/usa/arizona/">
            {_js('Mountain Standard Time')}
         </Link>
         .
      </Text>
   </Box>
);

const ActivityFilterView = ({
   userid,
   filters,
   initialPageContext,
}: {
   userid: number;
   filters: any;
   initialPageContext: any;
}) => {
   const [activeFilter, setActiveFilter] = useState('nofilter');
   const [pageContext, setPageContext] = useState(initialPageContext);

   const currentFilter = activeFilter || 'nofilter';
   const { data, isLoading } = useQuery({
      queryKey: [`users/${userid}/${pageContext.curPage}/${currentFilter}`],
   });

   if (!isLoading && (data as any).pageContext != pageContext) {
      setPageContext((data as any).pageContext);
   }

   const activityList = isLoading ? [] : (data as any).activities;
   const showPagination = pageContext.totalPages > 1;

   function changeFilter(newFilterName: string) {
      setActiveFilter(newFilterName);
      setPageContext({
         ...pageContext,
         curPage: 1,
      });
   }

   return (
      <Fragment>
         <Heading as="h3" my="8px" fontSize="18px" fontWeight="semibold">
            {_js('Filter Activity By:')}
         </Heading>
         <FilterOptions activeFilter={activeFilter} options={filters} changeFilter={changeFilter} />
         {isLoading ? (
            <LoadingList />
         ) : (
            <ActivityList showPagination={showPagination} activities={activityList} />
         )}
         {showPagination && (
            <Pagination pageContext={pageContext} setPageContext={setPageContext} />
         )}
      </Fragment>
   );
};

const LoadingList = () => {
   const LIST_SIZE = 40;
   return (
      <Stack my="8px">
         {Array.from(Array.from({ length: LIST_SIZE }), (e, i) => (
            <Skeleton key={i} height="19px" endColor="gray.300" />
         ))}
      </Stack>
   );
};

const ActivityList = ({
   showPagination,
   activities,
}: {
   showPagination: boolean;
   activities: any[];
}) => {
   const hasActivity = activities.length > 0;

   if (!hasActivity) {
      return <Text>{_js('No user activity')}</Text>;
   }
   return <ActivityHistory showPagination={showPagination} activities={activities} />;
};

const FilterOptions = ({
   activeFilter,
   options,
   changeFilter,
}: {
   activeFilter: any;
   options: any;
   changeFilter: (_newFilterName: string) => void;
}) => {
   const mainFilters = options.mainFilters;
   const extraFilters = options.extraFilters;

   return (
      <Flex align="center" flexWrap="wrap">
         <FiltersList
            filters={mainFilters}
            activeFilter={activeFilter}
            changeFilter={changeFilter}
         />
         <FiltersDropdown filters={extraFilters} changeFilter={changeFilter} />
      </Flex>
   );
};

const FiltersList = ({
   filters,
   activeFilter,
   changeFilter,
}: {
   filters: any;
   activeFilter: any;
   changeFilter: (_newFilterName: string) => void;
}) => {
   const activeButtonProps = {
      bgColor: 'brand.500',
      color: 'white',
      size: 'xs',
      fontSize: '12px',
      fontWeight: 'bold',
      _hover: {
         bgColor: 'brand.500',
      },
   };

   const inactiveButtonProps = {
      size: 'xs',
      fontSize: '12px',
      fontWeight: 'bold',
   };

   return filters.map((filter: any) => {
      const buttonProps = filter.name === activeFilter ? activeButtonProps : inactiveButtonProps;
      return (
         <Button
            {...buttonProps}
            key={filter.name}
            onClick={() => {
               changeFilter(filter.name);
            }}
         >
            {filter.label}
         </Button>
      );
   });
};

const FiltersDropdown = ({
   filters,
   changeFilter,
}: {
   filters: any;
   changeFilter: (_newFilterName: string) => void;
}) => (
   <Select
      w="170px"
      size="sm"
      placeholder={_js('More Filter Options')}
      onChange={e => {
         changeFilter(e.target.value);
      }}
   >
      {filters.map((filter: any) => (
         <option key={filter.name} value={filter.name}>
            {filter.label}
         </option>
      ))}
   </Select>
);

function groupActivitiesByDay(activities: any[]) {
   return groupBy(activities, (curActivity: any) => {
      const date = new Date(curActivity.date * 1000);
      return date.toLocaleDateString();
   });
}

const ActivityHistory = ({
   showPagination,
   activities,
}: {
   showPagination: boolean;
   activities: any[];
}) => {
   const history = groupActivitiesByDay(activities);
   return (
      <Flex direction="column" mt="-20px" mb={showPagination ? '8px' : '0'}>
         {Object.entries(history).map(([day, activities]) => (
            <ActivityHistoryGroup key={day} day={day} activities={activities} />
         ))}
      </Flex>
   );
};

const ActivityHistoryGroup = ({ day, activities }: { day: any; activities: any[] }) => (
   <Fragment>
      <Box bg="gray.100" p="5px" mt="24px">
         <Text m="0" fontWeight="semibold">
            {day}
         </Text>
      </Box>
      {activities.map((activity: any) => (
         <ActivityHistoryItem key={activity.id} activity={activity} />
      ))}
   </Fragment>
);

const ActivityHistoryItem = ({ activity }: { activity: any }) => {
   // Timestamps from the backend are off by a factor of 1000.
   const date = new Date(activity.date * 1000);
   const link = activity.link || activity.url;
   const badge = activity.image;
   const tier = activity['tier-icon'];
   const delta = activity.delta;
   const showDelta = delta && delta != 0;
   const id = activity.docid && activity.event ? `${activity.event}${activity.docid}` : null;

   return (
      // @ts-expect-error TS(2322) FIXME: Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
      <Flex mt="14px" align="center" id={id}>
         <Flex minW="40px" minH="40px" mr="6px" direction="column" justify="center">
            {badge && (
               <Flex h="100%" direction="column" align="center">
                  <Image w={25} h={25} src={badge} onClick={() => (window.location.href = link)} />
                  <Image w="6px" h="6px" src={tier} mt="5px" />
               </Flex>
            )}
            {showDelta ? (
               <Text
                  fontSize="18px"
                  fontWeight="semibold"
                  color={delta > 0 ? 'green.500' : 'red.500'}
               >
                  {delta > 0 ? `+${delta}` : delta}
               </Text>
            ) : null}
         </Flex>
         <Flex direction="column">
            {link ? (
               <Link fontSize="14px" color="brand.500" href={link} rel="nofollow">
                  {activity.text}
               </Link>
            ) : (
               <Text fontSize="14px" color="black">
                  {activity.text}
               </Text>
            )}
            <Text fontSize="12px" m="0">{`${date.toLocaleTimeString()}`}</Text>
         </Flex>
      </Flex>
   );
};
